// src/components/OrderDetailModal.tsx
import React, { useState } from 'react';
import {
  X, FileText, User, DollarSign, Calendar, Tags as TagsIcon,
  ClipboardList, Building2, Edit3, Copy, Clock, CheckCircle, Save, ArrowLeft
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

type OrderDetailClient = {
  name: string;
  platform?: string | null;
};

export type OrderDetail = {
  id: string;
  title: string;
  description?: string | null;
  budget?: number | null; // Changé de 'amount' à 'budget'
  due_date?: string | null; // Changé de 'deadline' à 'due_date'
  created_at?: string | null;

  // management
  status?: 'Pending' | 'In Progress' | 'Completed' | string;
  project_type?: string | null;
  priority_level?: string | null;
  estimated_hours?: number | null;
  hourly_rate?: number | null;
  payment_status?: string | null;

  // dates
  start_date?: string | null;
  completion_date?: string | null;
  revision_count?: number | null;

  // extras
  notes?: string | null;
  tags?: string[] | null;
  client_feedback?: string | null;

  // relation (comme dans OrdersPage)
  clients?: OrderDetailClient;

  // fallback éventuels si la page envoie des champs plats
  client_name?: string;
  platform?: string | null;
};

interface OrderDetailModalProps {
  order: OrderDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (order: OrderDetail) => void;
  onOrderUpdated?: (updatedOrder: OrderDetail) => void;
}

const Row: React.FC<{ label: string; value?: React.ReactNode; copyable?: boolean }> = ({ label, value, copyable = false }) => {
  const handleCopy = () => {
    if (typeof value === 'string') {
      navigator.clipboard.writeText(value);
    }
  };

  return (
    <div className="group flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 px-4 hover:bg-[rgb(var(--subtle))] rounded-lg transition-all duration-200">
      <div className="text-sm font-medium text-[rgb(var(--muted))] mb-1 sm:mb-0">{label}</div>
      <div className="flex items-center gap-2">
        <div className="text-sm text-[rgb(var(--fg))] break-words sm:ml-6">
          {value ?? <span className="text-[rgb(var(--muted-2))] italic">Not specified</span>}
        </div>
        {copyable && typeof value === 'string' && value !== 'Not specified' && (
          <button
            onClick={handleCopy}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-[rgb(var(--subtle))] rounded"
            title="Copy"
          >
            <Copy className="w-3 h-3 text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))]" />
          </button>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode; gradient?: boolean }> = ({ icon, title, children, gradient = false }) => (
  <div className={`
    ${gradient ? 'bg-[rgb(var(--subtle))] border-[rgb(var(--border-strong))]' : 'bg-[rgb(var(--card))] border-[rgb(var(--border))]'} 
    border rounded-xl p-5 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-md)] transition-all duration-300 hover:scale-[1.02]
  `}>
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 rounded-lg bg-[rgb(var(--subtle))]">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-[rgb(var(--fg))]">{title}</h3>
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const badgeForStatus = (status?: string) => {
  switch (status) {
    case 'Completed':
      return 'bg-[rgb(var(--card))] text-[rgb(var(--success))] border border-[rgb(var(--border-strong))]';
    case 'In Progress':
      return 'bg-[rgb(var(--card))] text-[rgb(var(--info))] border border-[rgb(var(--border-strong))]';
    case 'Pending':
      return 'bg-[rgb(var(--card))] text-[rgb(var(--warn))] border border-[rgb(var(--border-strong))]';
    default:
      return 'bg-[rgb(var(--card))] text-[rgb(var(--muted))] border border-[rgb(var(--border-strong))]';
  }
};

const chipForPlatform = (p?: string | null) =>
  p
    ? {
        Fiverr: 'bg-[rgb(var(--card))] text-[rgb(var(--success))] border border-[rgb(var(--border-strong))]',
        Upwork: 'bg-[rgb(var(--card))] text-[rgb(var(--info))] border border-[rgb(var(--border-strong))]',
        Direct: 'bg-[rgb(var(--card))] text-[rgb(var(--primary))] border border-[rgb(var(--border-strong))]',
      }[p] || 'bg-[rgb(var(--card))] text-[rgb(var(--info))] border border-[rgb(var(--border-strong))]'
    : 'bg-[rgb(var(--card))] text-[rgb(var(--muted))] border border-[rgb(var(--border-strong))]';

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ order, isOpen, onClose, onEdit, onOrderUpdated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    budget: '',
    status: 'Pending',
    start_date: '',
    due_date: '',
    completion_date: '',
    notes: '',
    tags: [] as string[],
  });

  // Initialiser les données du formulaire quand l'order change
  React.useEffect(() => {
    if (order) {
      setFormData({
        title: order.title || '',
        description: order.description || '',
        budget: order.budget?.toString() || '',
        status: order.status || 'Pending',
        start_date: order.start_date || '',
        due_date: order.due_date || '',
        completion_date: order.completion_date || '',
        notes: order.notes || '',
        tags: order.tags || [],
      });
    }
  }, [order]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Restaurer les données originales
    if (order) {
      setFormData({
        title: order.title || '',
        description: order.description || '',
        budget: order.budget?.toString() || '',
        status: order.status || 'Pending',
        start_date: order.start_date || '',
        due_date: order.due_date || '',
        completion_date: order.completion_date || '',
        notes: order.notes || '',
        tags: order.tags || [],
      });
    }
  };

  const handleSave = async () => {
    if (!order?.id) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('orders')
        .update({
          title: formData.title,
          description: formData.description,
          budget: formData.budget ? parseFloat(formData.budget) : null,
          status: formData.status,
          start_date: formData.start_date || null,
          due_date: formData.due_date || null,
          completion_date: formData.completion_date || null,
          notes: formData.notes,
          tags: formData.tags,
          updated_at: new Date().toISOString(),
        })
        .eq('id', order.id);

      if (error) throw error;

      toast.success('Order updated successfully');
      setIsEditing(false);
      
      // Notifier le parent de la mise à jour
      if (onOrderUpdated && order) {
        const updatedOrder = {
          ...order,
          title: formData.title,
          description: formData.description,
          budget: formData.budget ? parseFloat(formData.budget) : null,
          status: formData.status,
          start_date: formData.start_date,
          due_date: formData.due_date,
          completion_date: formData.completion_date,
          notes: formData.notes,
          tags: formData.tags,
        };
        onOrderUpdated(updatedOrder);
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  const clientName = order?.clients?.name || order?.client_name || '—';
  const platform = order?.clients?.platform ?? order?.platform ?? null;

  const tags = isEditing ? formData.tags : (order?.tags ?? []);
  const TagList = tags.length ? (
    <div className="flex flex-wrap gap-2">
      {tags.map((t, i) => (
        <span
          key={`${t}-${i}`}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full bg-[rgb(var(--card))] text-[rgb(var(--primary))] border border-[rgb(var(--border-strong))] hover:bg-[rgb(var(--subtle))] transition-all duration-200"
        >
          {t}
        </span>
      ))}
    </div>
  ) : (
    <span className="text-[rgb(var(--muted-2))] italic">No tags</span>
  );

  const computedTotal =
    (order?.estimated_hours ? Number(order.estimated_hours) : 0) *
    (order?.hourly_rate ? Number(order.hourly_rate) : 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      {/* Panel */}
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] shadow-[var(--shadow-md)]">
        <div className="sticky top-0 z-10 flex items-center justify-between px-8 py-6 border-b border-[rgb(var(--border))] bg-[rgb(var(--elevated))]">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-[rgb(var(--primary))]">
              <FileText className="w-6 h-6 text-[rgb(var(--primary-contrast))]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[rgb(var(--fg))]">
                {isEditing ? 'Edit Order' : (order?.title || 'Order')}
              </h2>
              {order?.created_at && (
                <p className="text-sm text-[rgb(var(--muted))]">
                  Created on {new Date(order.created_at).toLocaleDateString('en-US')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgb(var(--card))] text-[rgb(var(--fg))] border border-[rgb(var(--border))] text-sm font-medium hover:bg-[rgb(var(--subtle))] transition-all duration-200"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgb(var(--primary))] text-[rgb(var(--primary-contrast))] text-sm font-medium hover:opacity-90 focus:ring-2 focus:ring-[rgb(var(--primary))]/50 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-md)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <div className="w-4 h-4 border-2 border-[rgb(var(--primary-contrast))]/30 border-t-[rgb(var(--primary-contrast))] rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <>
                {onEdit && order && (
                  <button
                    onClick={handleEdit}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgb(var(--primary))] text-[rgb(var(--primary-contrast))] text-sm font-medium hover:opacity-90 focus:ring-2 focus:ring-[rgb(var(--primary))]/50 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-md)] transition-all duration-200"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[rgb(var(--subtle))] text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))] transition-all duration-200"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="p-8 space-y-6">
          <Section icon={<FileText className="w-5 h-5 text-[rgb(var(--primary))]" />} title="Order Details" gradient>
            {isEditing ? (
              <>
                <div className="py-3 px-4">
                  <label className="text-sm font-medium text-[rgb(var(--muted))] mb-2 block">Project title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted-2))] focus:border-[rgb(var(--primary))] focus:ring-2 focus:ring-[rgb(var(--primary))]/40 transition-all duration-200"
                    placeholder="Project title"
                  />
                </div>
                <div className="py-3 px-4">
                  <label className="text-sm font-medium text-[rgb(var(--muted))] mb-2 block">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] text-[rgb(var(--fg))] focus:border-[rgb(var(--primary))] focus:ring-2 focus:ring-[rgb(var(--primary))]/40 transition-all duration-200"
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                <div className="py-3 px-4">
                  <label className="text-sm font-medium text-[rgb(var(--muted))] mb-2 block">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted-2))] focus:border-[rgb(var(--primary))] focus:ring-2 focus:ring-[rgb(var(--primary))]/40 transition-all duration-200 resize-none"
                    placeholder="Project description"
                  />
                </div>
              </>
            ) : (
              <>
                <Row label="Project title" value={order?.title} copyable />
                <Row label="Project type" value={order?.project_type} />
                <Row
                  label="Status"
                  value={
                    <span className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full ${badgeForStatus(order?.status)}`}>
                      {order?.status || 'Not defined'}
                    </span>
                  }
                />
                <Row label="Priority level" value={order?.priority_level} />
                <Row label="Description" value={order?.description} />
              </>
            )}
          </Section>

          <Section icon={<User className="w-5 h-5 text-[rgb(var(--primary))]" />} title="Client Information">
            <Row label="Client name" value={clientName} copyable />
            <Row
              label="Platform"
              value={
                <span className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full ${chipForPlatform(platform)}`}>
                  {platform || 'Not specified'}
                </span>
              }
            />
          </Section>

          <Section icon={<DollarSign className="w-5 h-5 text-[rgb(var(--primary))]" />} title="Finance & Budget" gradient>
            {isEditing ? (
              <div className="py-3 px-4">
                <label className="text-sm font-medium text-[rgb(var(--muted))] mb-2 block">Total budget</label>
                <input
                  type="number"
                  value={formData.budget}
                  onChange={(e) => handleInputChange('budget', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted-2))] focus:border-[rgb(var(--primary))] focus:ring-2 focus:ring-[rgb(var(--primary))]/40 transition-all duration-200"
                  placeholder="Budget in $"
                />
              </div>
            ) : (
              <>
                <Row label="Total budget" value={typeof order?.budget === 'number' ? `$${order.budget.toLocaleString()}` : null} />
                <Row label="Payment status" value={order?.payment_status} />
                <Row label="Estimated hours" value={order?.estimated_hours ? `${order.estimated_hours}h` : null} />
                <Row label="Hourly rate" value={order?.hourly_rate ? `$${Number(order.hourly_rate).toFixed(2)}/h` : null} />
                <Row label="Calculated total" value={computedTotal ? `$${computedTotal.toFixed(2)}` : null} />
              </>
            )}
          </Section>

          <Section icon={<Calendar className="w-5 h-5 text-[rgb(var(--primary))]" />} title="Schedule & Deadlines">
            {isEditing ? (
              <>
                <div className="py-3 px-4">
                  <label className="text-sm font-medium text-[rgb(var(--muted))] mb-2 block">Start date</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleInputChange('start_date', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] text-[rgb(var(--fg))] focus:border-[rgb(var(--primary))] focus:ring-2 focus:ring-[rgb(var(--primary))]/40 transition-all duration-200"
                    />
                  </div>
                  <div className="py-3 px-4">
                  <label className="text-sm font-medium text-[rgb(var(--muted))] mb-2 block">Due date</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => handleInputChange('due_date', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] text-[rgb(var(--fg))] focus:border-[rgb(var(--primary))] focus:ring-2 focus:ring-[rgb(var(--primary))]/40 transition-all duration-200"
                    />
                  </div>
                  <div className="py-3 px-4">
                  <label className="text-sm font-medium text-[rgb(var(--muted))] mb-2 block">Completion date</label>
                    <input
                      type="date"
                      value={formData.completion_date}
                      onChange={(e) => handleInputChange('completion_date', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] text-[rgb(var(--fg))] focus:border-[rgb(var(--primary))] focus:ring-2 focus:ring-[rgb(var(--primary))]/40 transition-all duration-200"
                    />
                </div>
              </>
            ) : (
              <>
                <Row label="Start date" value={order?.start_date ? new Date(order.start_date).toLocaleDateString('en-US') : null} />
                <Row label="Due date" value={order?.due_date ? new Date(order.due_date).toLocaleDateString('en-US') : null} />
                <Row label="Completion date" value={order?.completion_date ? new Date(order.completion_date).toLocaleDateString('en-US') : null} />
                <Row label="Number of revisions" value={order?.revision_count ? `${order.revision_count} revision${order.revision_count > 1 ? 's' : ''}` : null} />
              </>
            )}
          </Section>

          <Section icon={<ClipboardList className="w-5 h-5 text-[#9c68f2]" />} title="Notes & Feedback">
            {isEditing ? (
              <div className="py-3 px-4">
                <label className="text-sm font-medium text-[rgb(var(--muted))] dark:text-[rgb(var(--muted))] mb-2 block">Internal notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--card))] border border-[#4a5568] text-[rgb(var(--fg))] placeholder-gray-400 focus:border-[#9c68f2] focus:ring-1 focus:ring-[#9c68f2] transition-all duration-200 resize-none"
                  placeholder="Internal notes on the project"
                />
              </div>
            ) : (
              <>
                <Row label="Internal notes" value={order?.notes} />
                <Row label="Client feedback" value={order?.client_feedback} />
              </>
            )}
          </Section>

          <Section icon={<TagsIcon className="w-5 h-5 text-[rgb(var(--primary))]" />} title="Tags & Categories">
            {isEditing ? (
              <div className="py-3 px-4">
                <label className="text-sm font-medium text-[rgb(var(--muted))] mb-2 block">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={formData.tags.join(', ')}
                  onChange={(e) => handleInputChange('tags', e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag))}
                  className="w-full px-3 py-2 rounded-lg bg-[rgb(var(--elevated))] border border-[rgb(var(--border))] text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted-2))] focus:border-[rgb(var(--primary))] focus:ring-2 focus:ring-[rgb(var(--primary))]/40 transition-all duration-200"
                  placeholder="design, logo, branding"
                />
                <p className="text-xs text-[rgb(var(--muted))] mt-1">Separate tags with commas</p>
              </div>
            ) : (
              <div className="py-2">
                {TagList}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
